<template>
    <flex-element
        class="nav f-flex"
        :element-type="elementType"
        :class="[directionClass, stateClasses]"
        class-default-inline="f-relative f-w-1/1 f-z-10"
    >
        <hamburger-button
            v-show="isMobile"
            class="nav__button"
            class-default="f-bg-gray-800 f-px-0.5 f-py-0.25"
            v-bind="element(3)"
            @element-updated="elementUpdated"
            @click.native="onMenuToggle"
        />

        <div
            class="nav__content f-flex f-flex-row f-flex-grow f-max-w-1/1 f-items-center"
            :class="bgColorClass"
            :style="transitionStyle"
        >
            <drop-zone-single class-default="f-w-auto" v-bind="element(1)" @element-updated="elementUpdated" />

            <nav-dropdown
                v-for="(element, index) in ddElements"
                :key="element.id"
                v-bind="element"
                :index="index + staticElemCount"
                :pointer="index + (staticElemCount + 1)"
                :is-mobile="isMobile"
                is-dynamic-element
                @element-updated="elementUpdated"
                @delete-element="onDelete"
            />

            <drop-zone-single class-default="f-w-auto" v-bind="element(2)" @element-updated="elementUpdated" />
        </div>

        <collection-controls
            location="outside"
            position="bottom-right"
            @add="onAdd"
            @delete="onDelete"
        />
    </flex-element>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import uuidv4 from 'uuid/v4';

import CollectionControls from '@/components/utilities/collection-controls';
import baseOrganism from '@/mixins/base-organism/base-organism-smart';
import { Organism } from '@/data/data-models';
import { CLEAR_SELECTED_ELEMENTS } from '@/store/types/mutation-types';
import flexDeepCopy from '@/utils/flex-deep-copy';
import { getClassDefinition } from '@/utils/tailwind-helper';
import { STATE_CLASSES } from '@/data/constants/classes';

const BREAKPOINT_ATTR_NAME = 'advanced-nav-breakpoint';
const SLIDE_DIRECTION_ATTR_NAME = 'hamburger-nav-slide-direction';
const SLIDE_SPEED_ATTR_NAME = 'hamburger-nav-slide-speed';

export default {
    name: 'nav-dropdown-collection-advanced',
    components: { CollectionControls },
    mixins: [baseOrganism],

    props: {
        displayName: {
            type: String,
            default: 'Nav Dropdown Collection',
        },
    },

    data() {
        return {
            isMobile: false,
            isCollapsed: true,
            staticElemCount: 3,
        };
    },

    computed: {
        ...mapState({
            deviceMode: state => state.page.deviceMode,
        }),

        bgColorClass() {
            return this.htmlClass.find((className) => {
                const classDefinition = getClassDefinition(className);
                const { tailwindDefinitionProp } = classDefinition;

                return tailwindDefinitionProp === 'backgroundColor';
            });
        },

        ddElements() {
            return this.elementsNew.slice(this.staticElemCount);
        },

        directionClass() {
            return `is-${ this.slideInDirection }`;
        },

        mobileModeBreakpoint() {
            return this.getElementData(BREAKPOINT_ATTR_NAME);
        },

        slideInDirection() {
            return this.getElementData(SLIDE_DIRECTION_ATTR_NAME);
        },

        slideInSpeed() {
            return this.getElementData(SLIDE_SPEED_ATTR_NAME);
        },

        stateClasses() {
            const classes = [];

            if (this.isMobile) classes.push(STATE_CLASSES.COMPACT);
            if (this.isCollapsed) classes.push(STATE_CLASSES.COLLAPSED);

            return classes;
        },

        transitionStyle() {
            return {
                transition: `all ${ this.slideInSpeed }s`,
            };
        },
    },

    watch: {
        deviceMode: {
            handler(deviceMode) {
                const deviceWidth = Number(deviceMode.deviceWidth.slice(0, -2));

                if (deviceWidth > 0 && deviceWidth <= this.mobileModeBreakpoint) {
                    this.isMobile = true;
                } else {
                    this.isMobile = false;
                }
            },
            deep: true,
        },

        isMobile(newVal, oldVal) {
            if (oldVal === true) this.isCollapsed = true;
        },
    },

    mounted() {
        if (this.mobileModeBreakpoint) {
            window.addEventListener('resize', this.onResize);
            this.detectMobile();
        }

        if (!this.ddElements.length) {
            this.onAdd(true);
            return;
        }
    },

    destroyed() {
        window.removeEventListener('resize', this.onResize);
    },

    methods: {
        ...mapMutations({
            clearSelectedElements: CLEAR_SELECTED_ELEMENTS,
        }),

        onAdd(isDefaultAdd) {
            const pointer = this.elementsNew.length + 1;
            let element = null;

            if (this.ddElements.length) {
                element = { ...this.elementsNew[this.elementsNew.length - 1] };

                element.id = uuidv4();
                element = flexDeepCopy(element);
                element.name = `nav-dropdown_${ pointer }`;
            } else {
                element = new Organism({
                    id: uuidv4(),
                    name: `nav-dropdown_${ pointer }`,
                    elementType: 'nav-dropdown',
                    pointer,
                    classes: '',
                    templateType: 'Organism',
                    dataSourceId: '00000000-0000-0000-0000-000000000000',
                    elements: [],
                });
            }

            this.elementsNew.push(element);
            if (!isDefaultAdd) this.setChangedData(true);
        },

        onDelete() {
            this.elementsNew.splice(this.elementsNew.length - 1, 1);
            this.clearSelectedElements();
            this.setChangedData(true);
        },

        onResize() {
            this.detectMobile();
        },

        onMenuToggle() {
            this.isCollapsed = !this.isCollapsed;
        },

        detectMobile() {
            const deviceWidth = this.deviceMode.name !== 'all'
                ? Number(this.deviceMode.deviceWidth.slice(0, -2))
                : window.innerWidth;

            const isMobile = window.innerWidth <= this.mobileModeBreakpoint || deviceWidth <= this.mobileModeBreakpoint;

            if (isMobile) {
                this.isMobile = true;
            } else {
                this.isMobile = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.nav {
    $this: &;

    &.#{ $is-compact } {
        align-items: start;
    }

    &.#{ $is-compact }.is-right {
        justify-content: flex-end;
    }

    &__button {
        display: none;
        position: absolute;
        top: 0.625rem;
        z-index: 10;

        #{ $this }.#{ $is-compact } & {
            display: block;
        }

        #{ $this }.#{ $is-compact }.is-left & {
            left: 0.625rem;
        }

        #{ $this }.#{ $is-compact }.is-right & {
            right: 0.625rem;
        }
    }

    &__content {
        position: relative;
        width: max-content;
        opacity: 1;
        left: 0;

        #{ $this }.#{ $is-compact } & {
            position: absolute;
            top: 100%;
            flex-direction: column;
            flex-grow: 1;
            width: 100%;
        }

        #{ $this }.#{ $is-compact }.#{ $is-collapsed }.is-left & {
            left: -100%;
            opacity: 0;
        }

        #{ $this }.#{ $is-compact }.#{ $is-collapsed }.is-right & {
            left: 100%;
            opacity: 0;
        }
    }
}
</style>
